import { useState, useEffect } from 'react';
import { X, Shield, Plus, Info, AlertCircle } from 'lucide-react';
import apiClient from '../api/client';
import { ProductCatalogDto, AssignProductDto } from '../types/dtos';

interface AssignProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    customerId: string;
    customerName: string;
    applicationId?: string; // If provided, we're updating an application
    preselectedProductId?: string;
    preselectedTierId?: string;
    initialStatus?: string;
}

export default function AssignProductModal({
    isOpen,
    onClose,
    onSuccess,
    customerId,
    customerName,
    applicationId,
    preselectedProductId,
    preselectedTierId,
    initialStatus = 'Active'
}: AssignProductModalProps) {
    const [products, setProducts] = useState<ProductCatalogDto[]>([]);
    const [selectedProduct, setSelectedProduct] = useState<ProductCatalogDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [formData, setFormData] = useState<AssignProductDto>({
        productId: preselectedProductId || '',
        instanceId: '',
        tierId: preselectedTierId || '',
        status: initialStatus,
        monthlyPremium: '',
        yearlyPremium: '',
        onceOffPremium: '',
        payout: '',
        premiumFrequency: 'Monthly',
        premiumDueDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
        expiryDate: '',
        principalInsuredMember: customerName,
        detailsJson: '',
        completeness: 100,
    });

    useEffect(() => {
        if (isOpen) {
            fetchProducts();
        }
    }, [isOpen]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get<ProductCatalogDto[]>('/admin/products');
            setProducts(res.data);

            // Handle pre-selection after products are loaded
            if (preselectedProductId) {
                const product = res.data.find(p => p.productId === preselectedProductId);
                if (product) {
                    setSelectedProduct(product);
                    setFormData(prev => ({
                        ...prev,
                        productId: product.productId,
                        monthlyPremium: product.monthlyPremium || '',
                        yearlyPremium: product.yearlyPremium || '',
                        onceOffPremium: product.onceOffPremium || '',
                        payout: product.payout || '',
                        premiumFrequency: product.premiumFrequency || 'Monthly',
                        expiryDate: product.policyDurationMonths
                            ? new Date(new Date().setMonth(new Date().getMonth() + product.policyDurationMonths)).toISOString().split('T')[0]
                            : '',
                    }));

                    if (preselectedTierId && product.tiers) {
                        const tier = product.tiers.find(t => t.id === preselectedTierId);
                        if (tier) {
                            setFormData(prev => ({
                                ...prev,
                                tierId: tier.id || '',
                                monthlyPremium: tier.monthlyPremium || '',
                                yearlyPremium: tier.yearlyPremium || '',
                                onceOffPremium: tier.onceOffPremium || '',
                                payout: tier.payout || '',
                            }));
                        }
                    }
                }
            }
        } catch (err) {
            console.error('Failed to fetch products', err);
            setError('Failed to load products list.');
        } finally {
            setLoading(false);
        }
    };

    const handleProductSelect = (productId: string) => {
        const product = products.find(p => p.productId === productId) || null;
        setSelectedProduct(product);

        if (product) {
            setFormData(prev => ({
                ...prev,
                productId: product.productId,
                monthlyPremium: product.monthlyPremium || 0,
                yearlyPremium: product.yearlyPremium || 0,
                onceOffPremium: product.onceOffPremium || 0,
                payout: product.payout || 0,
                premiumFrequency: product.premiumFrequency || 'Monthly',
                tierId: '', // Reset tier on product change
                expiryDate: product.policyDurationMonths
                    ? new Date(new Date().setMonth(new Date().getMonth() + product.policyDurationMonths)).toISOString().split('T')[0]
                    : '',
            }));
        }
    };

    const handleTierSelect = (tierId: string) => {
        if (!selectedProduct || !selectedProduct.tiers) return;
        const tier = selectedProduct.tiers.find(t => t.id === tierId);
        if (tier) {
            setFormData(prev => ({
                ...prev,
                tierId: tierId,
                monthlyPremium: tier.monthlyPremium,
                yearlyPremium: tier.yearlyPremium,
                onceOffPremium: tier.onceOffPremium || 0,
                payout: tier.payout,
                expiryDate: tier.policyDurationMonths
                    ? new Date(new Date().setMonth(new Date().getMonth() + tier.policyDurationMonths)).toISOString().split('T')[0]
                    : prev.expiryDate,
            }));
        } else {
            setFormData(prev => ({ ...prev, tierId: '' }));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        let finalValue: any = value;
        if (type === 'number') {
            finalValue = value === '' ? '' : value;
        }
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            const payload: any = { ...formData };
            // Convert numeric fields from strings to numbers for the API
            ['monthlyPremium', 'yearlyPremium', 'onceOffPremium', 'payout'].forEach(field => {
                if (payload[field] === '') {
                    payload[field] = 0;
                } else if (typeof payload[field] === 'string') {
                    payload[field] = parseFloat(payload[field]);
                }
            });

            if (payload.expiryDate === '') delete payload.expiryDate;
            if (payload.premiumDueDate === '') delete payload.premiumDueDate;

            if (applicationId) {
                // Update existing application
                await apiClient.put(`/admin/applications/${applicationId}/status`, {
                    status: formData.status,
                    newPolicyNumber: formData.instanceId || undefined,
                    quotedPremium: formData.premiumFrequency === 'Once-off' ? formData.onceOffPremium : formData.monthlyPremium,
                    quotedFrequency: formData.premiumFrequency,
                    notes: `Manually approved and assigned via admin portal assignment tool.`
                });
            } else {
                // Link new product
                await apiClient.post(`/admin/customers/${customerId}/products`, payload);
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Failed to assign product', err);
            let errorMessage = err.response?.data?.message || 'Failed to assign product. Please try again.';

            if (err.response?.data?.errors) {
                const errorMessages = Object.values(err.response.data.errors)
                    .flat()
                    .join(' ');
                errorMessage = `Validation Error: ${errorMessages}`;
            }

            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            {applicationId ? (initialStatus === 'Active' ? 'Approve & Activate' : 'Prepare Quote') : 'Assign Product'}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {applicationId ? `Processing application for ${customerName}` : `Assigning to ${customerName}`}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors font-bold text-gray-400">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
                            <AlertCircle className="text-red-500 shrink-0" size={20} />
                            <p className="text-sm text-red-700 font-medium">{error}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Product Selection */}
                        {!preselectedProductId ? (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Select Product Policy <span className="text-red-500">*</span></label>
                                <select
                                    required
                                    className="w-full rounded-xl border-gray-300 border px-4 py-2.5 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                                    value={formData.productId}
                                    onChange={(e) => handleProductSelect(e.target.value)}
                                >
                                    <option value="">-- Choose a Product --</option>
                                    {products.map(p => (
                                        <option key={p.productId} value={p.productId}>{p.name} ({p.productId})</option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <div className="md:col-span-2 bg-blue-50 p-4 rounded-xl border border-blue-100 flex items-center justify-between">
                                <div>
                                    <label className="text-[10px] font-bold text-blue-900 uppercase block">Selected Product</label>
                                    <div className="text-lg font-bold text-gray-900">{selectedProduct?.name || preselectedProductId}</div>
                                </div>
                                <Shield className="text-blue-900 opacity-20" size={40} />
                            </div>
                        )}

                        {/* Tier Selection (Conditional) */}
                        {selectedProduct?.hasTiers && (
                            <div className="md:col-span-2 animate-in slide-in-from-top-2 duration-300">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Select Tier</label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {selectedProduct.tiers?.map(t => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => handleTierSelect(t.id || '')}
                                            className={`p-3 rounded-xl border-2 text-left transition-all ${formData.tierId === t.id
                                                ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-100'
                                                : 'border-gray-100 bg-gray-50 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="font-bold text-sm text-gray-900">{t.name}</div>
                                            <div className="text-xs text-gray-500">MK {t.monthlyPremium.toLocaleString()}/mo</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Customization Section Title */}
                        <div className="md:col-span-2 border-t pt-4 mt-2">
                            <h4 className="text-sm font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                <Info size={14} /> Customization & Policy Details
                            </h4>
                        </div>

                        {/* Instance/Policy ID */}
                        {formData.status !== 'Quote Ready' && (
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Policy ID / Instance ID</label>
                                <input
                                    type="text"
                                    name="instanceId"
                                    placeholder="Auto-generated if empty"
                                    className="w-full rounded-xl border-gray-300 border px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.instanceId}
                                    onChange={handleChange}
                                />
                            </div>
                        )}

                        {/* Status */}
                        {!applicationId && (
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Current Status</label>
                                <select
                                    name="status"
                                    className="w-full rounded-xl border-gray-300 border px-4 py-2 bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.status}
                                    onChange={handleChange}
                                >
                                    <option value="Active">Active</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Lapsed">Lapsed</option>
                                    <option value="Cancelled">Cancelled</option>
                                </select>
                            </div>
                        )}

                        {/* Monthly Premium */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Monthly Premium (MK)</label>
                            <input
                                type="number"
                                name="monthlyPremium"
                                className="w-full rounded-xl border-gray-300 border px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.monthlyPremium}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Yearly Premium */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Yearly Premium (MK)</label>
                            <input
                                type="number"
                                name="yearlyPremium"
                                className="w-full rounded-xl border-gray-300 border px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.yearlyPremium}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Once-off Premium */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Once-off Premium (MK)</label>
                            <input
                                type="number"
                                name="onceOffPremium"
                                className="w-full rounded-xl border-gray-300 border px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.onceOffPremium}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Payout */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Payout Benefit (MK)</label>
                            <input
                                type="number"
                                name="payout"
                                className="w-full rounded-xl border-gray-300 border px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.payout}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Principal Insured */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-1">Principal Insured Member</label>
                            <input
                                type="text"
                                name="principalInsuredMember"
                                className="w-full rounded-xl border-gray-300 border px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.principalInsuredMember}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Next Due Date */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Next Premium Due Date</label>
                            <input
                                type="date"
                                name="premiumDueDate"
                                className="w-full rounded-xl border-gray-300 border px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.premiumDueDate}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Expiry Date */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Policy Expiry Date</label>
                            <input
                                type="date"
                                name="expiryDate"
                                className="w-full rounded-xl border-gray-300 border px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.expiryDate}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Frequency */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Payment Frequency</label>
                            <select
                                name="premiumFrequency"
                                className="w-full rounded-xl border-gray-300 border px-4 py-2 bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.premiumFrequency}
                                onChange={handleChange}
                            >
                                <option value="Monthly">Monthly</option>
                                <option value="Quarterly">Quarterly</option>
                                <option value="Yearly">Yearly</option>
                                <option value="Once-off">Once-off</option>
                            </select>
                        </div>

                        {/* Completeness */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">Completeness (%)</label>
                            <input
                                type="number"
                                name="completeness"
                                min="0"
                                max="100"
                                className="w-full rounded-xl border-gray-300 border px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                value={formData.completeness}
                                onChange={handleChange}
                            />
                        </div>

                    </div>
                </form>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2 rounded-xl text-gray-600 font-bold hover:bg-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || !formData.productId}
                        className={`px-8 py-2 rounded-xl font-bold text-white shadow-lg transition-all flex items-center gap-2 ${submitting || !formData.productId ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-900 hover:bg-blue-800 active:scale-95'
                            }`}
                    >
                        {submitting ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Processing...
                            </>
                        ) : (
                            <>
                                {applicationId ? (formData.status === 'Active' ? 'Confirm Activation' : 'Generate Quote') : (
                                    <><Plus size={18} /> Assign Policy</>
                                )}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
